import { useState } from 'react';
import { History } from 'lucide-react';
import { DashboardPageFormBody, DashboardPageFormShell } from '@shigo-ui/dashboard-ui';
import { Button, Skeleton, toast } from '@shigo-ui/ui';

import { ReturnBackLink } from '../../../../../components/navigation/ReturnBackLink';
import { useReturnNavigation } from '../../../../../lib/returnNavigation';
import { showRequestError } from '../../../../../lib/requestErrorToast';
import { dashboardT } from '../../../../../lib/messages';
import { ShigoPermission } from '../../../../auth/permissions';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { ChangelogDialog } from '../../../../changelog/components/ChangelogDialog';
import { demandPartnerToFormValue, demandPartnerToPayload } from '../../formModel';
import { emptyDemandPartnerFormValue, type DemandPartnerFormValue } from '../../formSchema';
import { useDemandPartnerMutations, useDemandPartnerQuery } from '../../hooks';
import { DemandPartnerEditorForm } from './DemandPartnerEditorForm';

export function DemandPartnerEditorPage(props: { demandPartnerId?: string }) {
  const { returnTo } = useReturnNavigation({ to: '/demand/partners' });
  const { can } = useShigoPermissions();
  const [changelogOpen, setChangelogOpen] = useState(false);
  const editing = Boolean(props.demandPartnerId);
  const query = useDemandPartnerQuery(props.demandPartnerId ?? '');
  const changelogPartner =
    props.demandPartnerId && query.isFetchedAfterMount && !query.isError ? query.data : undefined;
  const canReadChangelog = can(ShigoPermission.DemandPartnersChangelogRead);
  const mutations = useDemandPartnerMutations();
  const saving = mutations.create.isPending || mutations.update.isPending;
  const waitingForFreshDetail = editing && !query.isFetchedAfterMount;
  const initialValue = query.data
    ? demandPartnerToFormValue(query.data)
    : emptyDemandPartnerFormValue;

  async function save(value: DemandPartnerFormValue) {
    try {
      const payload = demandPartnerToPayload(value, {
        originalOwnerId: query.data?.ownerId ?? query.data?.owner?.id,
      });
      if (props.demandPartnerId) {
        await mutations.update.mutateAsync({ id: props.demandPartnerId, payload });
      } else {
        await mutations.create.mutateAsync(payload);
      }
      toast.success(editing ? 'Demand Partner updated.' : 'Demand Partner created.');
      await returnTo({ replace: true });
    } catch (error) {
      showRequestError(error, 'Demand Partner could not be saved.');
    }
  }

  let content;
  if (waitingForFreshDetail && !query.isError) {
    content = (
      <div role="status" aria-label="Loading Demand Partner" className="grid gap-4 p-6">
        <Skeleton className="h-10 w-72 max-w-full" />
        <Skeleton className="h-80 w-full" />
      </div>
    );
  } else if (editing && query.isError) {
    content = (
      <DashboardPageFormBody title="Demand Partner could not be loaded.">
        <Button type="button" variant="outline" onClick={() => void query.refetch()}>
          Retry
        </Button>
      </DashboardPageFormBody>
    );
  } else {
    content = (
      <DemandPartnerEditorForm
        key={props.demandPartnerId ?? 'create'}
        initialValue={initialValue}
        bundleListCount={query.data?.bundleListCount ?? 0}
        domainListCount={query.data?.domainListCount ?? 0}
        saving={saving}
        onCancel={() => void returnTo()}
        onSubmit={save}
      />
    );
  }
  return (
    <>
      <DashboardPageFormShell
        title={dashboardT(editing ? 'demandPartners.editTitle' : 'demandPartners.createTitle')}
        headerActions={
          changelogPartner && canReadChangelog ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setChangelogOpen(true);
              }}
            >
              <History className="h-4 w-4" aria-hidden="true" />
              {dashboardT('demandPartners.changelog')}
            </Button>
          ) : undefined
        }
        backAction={
          <ReturnBackLink fallback={{ to: '/demand/partners' }} label="Back to Demand Partners" />
        }
      >
        {content}
      </DashboardPageFormShell>
      {changelogPartner && canReadChangelog && changelogOpen ? (
        <ChangelogDialog
          title={dashboardT('demandPartners.changelogTitle', {
            name: changelogPartner.displayName ?? changelogPartner.name ?? '',
          })}
          description={dashboardT('demandPartners.changelogDescription')}
          scope={{ demandPartnerIds: [changelogPartner.id] }}
          onClose={() => {
            setChangelogOpen(false);
          }}
        />
      ) : null}
    </>
  );
}
